import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Wallet, Copy, Check, Gift, Calendar, TrendingDown, TrendingUp,
  User, Mail, Phone, MapPin, Edit3, Save, X, History, BookOpen, IndianRupee,
  Hotel as HotelIcon, LayoutDashboard, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const daysRemaining = (expiresAt) => {
  if (!expiresAt) return null;
  const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
  return days >= 0 ? days : 0;
};

const STATUS_STYLE = {
  pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  confirmed: 'bg-[#FFE5EE] text-[#CC0047] border-[#FF0059]/30',
  checked_in: 'bg-blue-50 text-blue-700 border-blue-200',
  checked_out: 'bg-green-50 text-green-700 border-green-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
  no_show: 'bg-red-50 text-red-700 border-red-200',
};

export const ProfilePage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', address: '' });
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(() => {
    // hotel users default to 'hotel' mode; everyone else to 'guest'
    return user?.role === 'hotel' ? 'hotel' : 'guest';
  });
  const isHotelUser = user?.role === 'hotel';

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/guest/profile`, { withCredentials: true });
      setProfile(data);
      setForm({ name: data.name || '', phone: data.phone || '', address: data.address || '' });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const copyCode = () => {
    if (!profile?.guest_code) return;
    navigator.clipboard.writeText(profile.guest_code);
    setCopied(true);
    toast.success(t('profile.codeCopied'));
    setTimeout(() => setCopied(false), 2000);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/guest/profile`, form, { withCredentials: true });
      toast.success('Profile updated');
      setEditing(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !profile) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF0059]"></div>
      </div>
    );
  }

  const balance = profile.wallet?.balance || 0;
  const credits = profile.wallet?.active_credits || [];
  const txns = profile.wallet?.transactions || [];
  const recentBookings = profile.recent_bookings || [];
  const totalBookings = profile.total_bookings || 0;

  return (
    <div className="min-h-screen py-8 sm:py-10 bg-[#FAFAFA]" data-testid="profile-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-black mb-1" data-testid="profile-title">{t('profile.title')}</h1>
            <p className="text-gray-600 text-sm sm:text-base">Manage your account, wallet and bookings — all in one place.</p>
          </div>
          {isHotelUser && (
            <div className="inline-flex p-1 rounded-full bg-white border border-gray-200 shadow-sm self-start sm:self-end" data-testid="profile-mode-switcher">
              <button
                type="button"
                onClick={() => setMode('guest')}
                className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-bold transition-colors ${mode === 'guest' ? 'bg-[#FF0059] text-white' : 'text-gray-700 hover:text-black'}`}
                data-testid="mode-guest-btn"
              >
                Guest Mode
              </button>
              <button
                type="button"
                onClick={() => setMode('hotel')}
                className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-bold transition-colors ${mode === 'hotel' ? 'bg-[#FF0059] text-white' : 'text-gray-700 hover:text-black'}`}
                data-testid="mode-hotel-btn"
              >
                Hotel Mode
              </button>
            </div>
          )}
        </div>

        {/* ============ HOTEL MODE ============ */}
        {isHotelUser && mode === 'hotel' && (
          <div className="space-y-6" data-testid="hotel-mode-content">
            <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-7 shadow-sm" data-testid="hotel-identity-card">
              <div className="flex items-start gap-4 sm:gap-5">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-[#FF0059] to-[#CC0047] text-white flex items-center justify-center flex-shrink-0 shadow-md">
                  <HotelIcon className="h-8 w-8" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl sm:text-2xl font-black text-black tracking-tight truncate">{user?.hotel_name || user?.name}</h2>
                  <p className="text-xs text-gray-500 mt-1 truncate">{user?.hotel_address || user?.city_area || '—'}</p>
                  <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FFE5EE] text-[10px] sm:text-xs font-bold text-[#CC0047]">
                    <Sparkles className="h-3 w-3" /> HOTEL OWNER
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-5">
                <InfoRow icon={User} label="Owner name" value={user?.name} editing={false} />
                <InfoRow icon={Mail} label="Email" value={user?.email} editing={false} />
                <InfoRow icon={Phone} label="Contact" value={user?.phone || '—'} editing={false} />
                <InfoRow icon={MapPin} label="Property address" value={user?.hotel_address || '—'} editing={false} />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link to="/hotel/dashboard" className="bg-white border border-gray-200 rounded-2xl p-5 hover:border-[#FF0059] hover:shadow-md transition-all group" data-testid="hotel-link-admin">
                <div className="w-10 h-10 rounded-xl bg-[#FFE5EE] flex items-center justify-center mb-3 group-hover:bg-[#FF0059] transition-colors">
                  <LayoutDashboard className="h-5 w-5 text-[#FF0059] group-hover:text-white" />
                </div>
                <h3 className="font-black text-black">Open Hotel Admin</h3>
                <p className="text-xs text-gray-500 mt-1">Reservations, calendar, photos &amp; more.</p>
              </Link>
              {user?._id && (
                <Link to={`/hotels/${user._id}`} className="bg-white border border-gray-200 rounded-2xl p-5 hover:border-[#FF0059] hover:shadow-md transition-all group" data-testid="hotel-link-listing">
                  <div className="w-10 h-10 rounded-xl bg-[#FFF9DB] flex items-center justify-center mb-3 group-hover:bg-[#FFCB00] transition-colors">
                    <Sparkles className="h-5 w-5 text-[#997800]" />
                  </div>
                  <h3 className="font-black text-black">See your Listing</h3>
                  <p className="text-xs text-gray-500 mt-1">View your public hotel page as a guest sees it.</p>
                </Link>
              )}
              <button
                type="button"
                onClick={() => setMode('guest')}
                className="bg-white border border-gray-200 rounded-2xl p-5 hover:border-[#FF0059] hover:shadow-md transition-all group text-left"
                data-testid="hotel-link-guest-mode"
              >
                <div className="w-10 h-10 rounded-xl bg-[#FFE5EE] flex items-center justify-center mb-3 group-hover:bg-[#FF0059] transition-colors">
                  <User className="h-5 w-5 text-[#FF0059] group-hover:text-white" />
                </div>
                <h3 className="font-black text-black">Switch to Guest Mode</h3>
                <p className="text-xs text-gray-500 mt-1">Book stays &amp; redeem wallet credits like any other guest.</p>
              </button>
            </div>
          </div>
        )}

        {/* ============ GUEST MODE (default for guests, optional for hotels) ============ */}
        {(!isHotelUser || mode === 'guest') && (
        <div data-testid="guest-mode-content">

        {/* ============ Identity / Personal Info ============ */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-7 shadow-sm mb-6" data-testid="identity-card">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div className="flex items-start gap-4 sm:gap-5 flex-1 min-w-0">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-[#FF0059] to-[#CC0047] text-white flex items-center justify-center text-2xl sm:text-3xl font-black flex-shrink-0 shadow-md">
                {(profile.name || 'G').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl sm:text-2xl font-black text-black tracking-tight truncate" data-testid="profile-name">{profile.name}</h2>
                {profile.guest_code && (
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#FFE5EE] border border-[#FF0059]/30" data-testid="guest-code-pill">
                    <Gift className="h-4 w-4 text-[#FF0059] flex-shrink-0" />
                    <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-[#CC0047]">{t('profile.guestCode')}</span>
                    <span className="font-mono font-black text-sm sm:text-base text-black tracking-widest" data-testid="guest-code-value">{profile.guest_code}</span>
                    <button
                      onClick={copyCode}
                      className="ml-1 p-1 rounded hover:bg-white transition-colors"
                      aria-label="Copy guest code"
                      data-testid="copy-guest-code"
                    >
                      {copied ? <Check className="h-4 w-4 text-[#FF0059]" /> : <Copy className="h-4 w-4 text-gray-500" />}
                    </button>
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-2 leading-relaxed">{t('profile.guestCodeHint')}</p>
              </div>
            </div>
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full border border-gray-200 hover:border-[#FF0059] text-xs sm:text-sm font-bold text-black transition-colors flex-shrink-0"
                data-testid="profile-edit-btn"
              >
                <Edit3 className="h-3.5 w-3.5" /> Edit
              </button>
            ) : (
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => { setEditing(false); setForm({ name: profile.name || '', phone: profile.phone || '', address: profile.address || '' }); }}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  aria-label="Cancel"
                  data-testid="profile-cancel-btn"
                >
                  <X className="h-4 w-4 text-gray-600" />
                </button>
                <button
                  onClick={saveProfile}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#FF0059] hover:bg-[#CC0047] disabled:opacity-60 text-white text-xs sm:text-sm font-bold transition-colors"
                  data-testid="profile-save-btn"
                >
                  <Save className="h-3.5 w-3.5" /> {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-2">
            <InfoRow icon={User} label="Full Name" value={profile.name} editing={editing}
              input={<input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] focus:ring-2 focus:ring-[#FFE5EE] outline-none text-sm font-medium"
                data-testid="profile-name-input" />}
            />
            <InfoRow icon={Mail} label="Email" value={profile.email} editing={false} />
            <InfoRow icon={Phone} label="Contact Number" value={profile.phone || '—'} editing={editing}
              input={<input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+91 …" className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] focus:ring-2 focus:ring-[#FFE5EE] outline-none text-sm font-medium"
                data-testid="profile-phone-input" />}
            />
            <InfoRow icon={MapPin} label="Address" value={profile.address || '—'} editing={editing} fullWidth
              input={<textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Street, city, pincode…" rows={2}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#FF0059] focus:ring-2 focus:ring-[#FFE5EE] outline-none text-sm font-medium resize-none"
                data-testid="profile-address-input" />}
            />
          </div>
        </div>

        {/* ============ Wallet Hero ============ */}
        <div className="bg-gradient-to-br from-[#FF0059] to-[#CC0047] rounded-2xl p-5 sm:p-7 text-white relative overflow-hidden mb-6 shadow-lg" data-testid="wallet-card">
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#FF4D8A] rounded-full opacity-20 blur-3xl" />
          <div className="relative grid sm:grid-cols-2 gap-4 items-center">
            <div>
              <div className="flex items-center gap-2 text-white/80 text-xs sm:text-sm font-bold mb-2">
                <Wallet className="h-4 w-4" /> LIINK WALLET
              </div>
              <div className="text-4xl sm:text-5xl font-black mb-1" data-testid="wallet-balance">{formatINR(balance)}</div>
              <p className="text-xs sm:text-sm text-white/80">{t('profile.walletBalance')} · Earn 5% on every stay</p>
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur text-[11px] sm:text-xs font-bold">
                <Gift className="h-3.5 w-3.5" /> Credits expire 30 days after earned
              </div>
            </div>
            <div className="sm:text-right" data-testid="wallet-stats">
              <div className="grid grid-cols-2 sm:grid-cols-1 gap-3 text-left sm:text-right">
                <div>
                  <p className="text-[11px] text-white/60 font-bold uppercase tracking-wider">Active Credits</p>
                  <p className="text-2xl font-black">{credits.length}</p>
                </div>
                <div>
                  <p className="text-[11px] text-white/60 font-bold uppercase tracking-wider">Total Bookings</p>
                  <p className="text-2xl font-black">{totalBookings}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* ============ Booking History ============ */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6" data-testid="bookings-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg sm:text-xl font-black text-black flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-[#FF0059]" />
                Booking History
              </h3>
              {recentBookings.length > 0 && (
                <Link to="/my-bookings" className="text-xs font-bold text-[#FF0059] hover:underline" data-testid="see-all-bookings-link">
                  See all →
                </Link>
              )}
            </div>
            {recentBookings.length === 0 ? (
              <div className="text-center py-8" data-testid="no-bookings">
                <BookOpen className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-500 mb-3">No bookings yet — start exploring!</p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#FF0059] hover:bg-[#CC0047] text-white text-xs font-bold transition-colors"
                  data-testid="discover-hotels-link"
                >
                  Discover hotels
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 -my-2">
                {recentBookings.map((b) => (
                  <Link
                    to="/my-bookings"
                    key={b._id}
                    className="block py-3 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors"
                    data-testid={`booking-row-${b._id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-black truncate">{b.hotel_name}</p>
                        <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {b.city_area || '—'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDate(b.check_in)} → {formatDate(b.check_out)}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_STYLE[b.status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                          {(b.status || 'pending').replace('_', ' ')}
                        </span>
                        <p className="font-black text-sm text-black mt-1 inline-flex items-center">
                          <IndianRupee className="h-3 w-3" />{Number(b.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* ============ Active Credits ============ */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6" data-testid="active-credits-card">
            <h3 className="text-lg sm:text-xl font-black text-black mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#FF0059]" />
              Active Credits ({credits.length})
            </h3>
            {credits.length === 0 ? (
              <div className="text-center py-8" data-testid="no-credits">
                <Gift className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-500">
                  You haven't earned any wallet credits yet. Book your first stay to start earning 5% rewards.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 -my-2">
                {credits.map((c, idx) => (
                  <div key={idx} className="py-3 flex items-center justify-between" data-testid={`credit-${idx}`}>
                    <div>
                      <p className="font-bold text-black">{formatINR(c.amount_remaining)}</p>
                      <p className="text-xs text-gray-500">Earned {formatDate(c.earned_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold text-[#FF0059]">{daysRemaining(c.expires_at)} days left</p>
                      <p className="text-xs text-gray-500">Expires {formatDate(c.expires_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ============ Transaction History (full width) ============ */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 mt-6" data-testid="transactions-card">
          <h3 className="text-lg sm:text-xl font-black text-black mb-4 flex items-center gap-2">
            <History className="h-5 w-5 text-[#FF0059]" />
            Wallet Transaction History
          </h3>
          {txns.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6" data-testid="no-transactions">
              {t('profile.noActivity')}
            </p>
          ) : (
            <div className="divide-y divide-gray-100 -my-2">
              {txns.map((tx) => (
                <div key={tx._id} className="py-3 flex items-center justify-between gap-3" data-testid={`txn-${tx._id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${tx.type === 'earn' ? 'bg-[#FFE5EE]' : 'bg-red-50'}`}>
                      {tx.type === 'earn' ? (
                        <TrendingUp className="h-4 w-4 text-[#FF0059]" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-sm text-black truncate">
                        {tx.type === 'earn' ? 'Earned from stay' : 'Redeemed on booking'}
                      </p>
                      <p className="text-xs text-gray-500">{formatDate(tx.created_at)}</p>
                    </div>
                  </div>
                  <p className={`font-black text-sm flex-shrink-0 ${tx.amount > 0 ? 'text-[#FF0059]' : 'text-red-500'}`}>
                    {tx.amount > 0 ? '+' : ''}{formatINR(Math.abs(tx.amount))}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
        )}
      </div>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value, editing, input, fullWidth }) => (
  <div className={fullWidth ? 'sm:col-span-2' : ''}>
    <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1.5">
      <Icon className="h-3 w-3" /> {label}
    </p>
    {editing && input ? input : (
      <p className="text-sm font-semibold text-black break-words">{value || '—'}</p>
    )}
  </div>
);
